import { Stack } from "@mui/material";
import objectHash from "object-hash";
import { useCallback, useMemo, useState } from "react";
import DataLoadingFailed from "@entrilia/shared/components/DataLoadingFailed";
import useFetch from "@entrilia/shared/hooks/useFetch";
import { logError } from "@entrilia/shared/logging";
import adminApi from "../../../../../api/adminApi";
import useClientContext from "../../../../../context/useClientContext";
import { FilterContextProvider } from "../../../../common/filters/FilterContextProvider";
import FiltersPanel from "../../../../common/filters/FiltersPanel";
import { getInitialFilterState } from "../../../../common/filters/filterState";
import ActivityLogGrid from "./ActivityLogGrid";
import {
  activityLogFilterDefinitions,
  getActivityLogsFilterPayload,
  getInitialActivityLogViewPagingState,
} from "./activityLogViewState";

const ActivityLogView = () => {
  const { clientCode } = useClientContext();

  const [pagingState, setPagingState] = useState(getInitialActivityLogViewPagingState);

  const [filterState, setFilterState] = useState(() =>
    getInitialFilterState(`${clientCode}_activity_log_view_filter_v1`, activityLogFilterDefinitions, [])
  );

  const filterPayload = useMemo(() => getActivityLogsFilterPayload(filterState), [filterState]);

  const filterPayloadHash = objectHash(filterPayload, { unorderedArrays: true });
  const getRows = useCallback(
    () =>
      adminApi.searchActivityLogs({
        paging: {
          totals: pagingState.pageIndex === 0,
          page: pagingState.pageIndex,
          size: pagingState.rowsPerPage,
        },
        ...filterPayload,
      }),
    // eslint-disable-next-line @eslint-react/exhaustive-deps
    [pagingState.pageIndex, pagingState.rowsPerPage, filterPayloadHash]
  );

  const [recordsResp, fetchError, { isFetching, fetch }] = useFetch(getRows, {
    onSetData: (data) => {
      if (data.totalPages !== undefined && data.total !== undefined) {
        setPagingState((prev) => ({ ...prev, totalPages: data.totalPages, totalItems: data.total }));
      }
    },
  });

  const handlePageIndexChange = useCallback((pageIndex: number) => {
    setPagingState((prev) => ({ ...prev, pageIndex }));
  }, []);

  const handleRowsPerPageChange = useCallback((rowsPerPage: number) => {
    setPagingState((prev) => ({ ...prev, rowsPerPage, pageIndex: 0 }));
  }, []);

  if (fetchError) {
    logError(fetchError, "[ActivityLogView] getRows");
    return <DataLoadingFailed title="Failed to load log records" />;
  }

  const rows = recordsResp?.items || [];

  return (
    <FilterContextProvider filterState={filterState} setFilterState={setFilterState}>
      <Stack spacing={2.5} sx={{ height: "100%" }}>
        <FiltersPanel
          withoutSearch
          recordCount={rows.length}
          totalCount={pagingState.totalItems}
          isLoading={isFetching}
          onRefresh={fetch}
        />

        <ActivityLogGrid
          rows={rows}
          isLoading={isFetching}
          pagingState={pagingState}
          onPageIndexChange={handlePageIndexChange}
          onRowsPerPageChange={handleRowsPerPageChange}
        />
      </Stack>
    </FilterContextProvider>
  );
};

export default ActivityLogView;
